import { FormEvent, useState } from 'react';
import { Col, Container, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Btn, H2 } from '../../../../AbstractElements';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';
import { clientSignupService } from '../../../../Services/Auth';
import MobileField from '../../../common/MobileField';
import PasswordInput from '../../../common/PasswordInput';
import TextField from '../../../common/TextField';

export default function MMPSignup() {
    const navigate = useNavigate();
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [consent, setConsent] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [formData, setFormData] = useState({
        client1FirstName: '',
        client1LastName: '',
        client1Email: '',
        client1Mobile: '',
        password: '',
        confirmPassword: ''
    });

    const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);
    const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!isConfirmPasswordVisible);

    const { mutate: signup, isPending } = useMutationRequestQuery(
        'clientSignup',
        clientSignupService,
        {
            onSuccess: () => {
                toast.success("Signup successful! Please login.");
                navigate(`${process.env.PUBLIC_URL}/MMPLogin`);
            },
            onError: (error: any) => {
                toast.error("Signup failed. User alredy Exists.");
            },
        }
    );

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Please consent to the terms and conditions");
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }
        // Remove confirmPassword before sending to API
        const { confirmPassword, ...apiData } = formData;
        signup(apiData);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        if (name === 'client1LastName') {
            // Convert surname to all uppercase
            setFormData(prev => ({
                ...prev,
                [name]: value.toUpperCase()
            }));
        } else if (name === 'client1Email') {
            // Keep email as is without capitalization
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        } else {
            // Capitalize each word for other fields
            const capitalizedValue = value.split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
            
            setFormData(prev => ({
                ...prev,
                [name]: capitalizedValue
            }));
        }
    };

    return (
        <Container className="p-0" fluid>
            <Row className="m-0">
                <Col xs={12} className="p-0">
                    <div className="login-card login-dark">
                        <div>
                            <div className="login-main">
                                <Form className="theme-form" onSubmit={handleSubmit}>
                                    <div className='text-center'>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/assets/images/fintrack_logo.png`}
                                            alt="Fintrack Logo"
                                            style={{ maxWidth: '230px', height: 'auto' }}
                                        />
                                    </div>
                                    <H2 className="text-center heading-color my-4">Mortgage Mastery Program</H2>
                                    <FormGroup>
                                        <Col><Label>First Name</Label></Col>
                                        <TextField
                                            type="text"
                                            name="client1FirstName"
                                            value={formData.client1FirstName}
                                            setValue={(name, value) => setFormData(prev => ({
                                                ...prev,
                                                [name]: value
                                            }))}
                                            maxChars={50}
                                            required
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Col><Label>Surname</Label></Col>
                                        <TextField
                                            type="text"
                                            name="client1LastName"
                                            value={formData.client1LastName}
                                            setValue={(name, value) => setFormData(prev => ({
                                                ...prev,
                                                [name]: value.toUpperCase()
                                            }))}
                                            maxChars={50}
                                            required
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Col><Label>Email</Label></Col>
                                        <Input
                                            type="email"
                                            name="client1Email"
                                            value={formData.client1Email}
                                            onChange={handleChange}
                                            maxLength={50}
                                            required
                                            autoComplete="off"
                                            autoCapitalize="off"
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Col><Label>Phone Number</Label></Col>
                                        <MobileField
                                            type="tel"
                                            mobileField="client1Mobile"
                                            value={formData.client1Mobile}
                                            setValue={(field, value) => setFormData(prev => ({
                                                ...prev,
                                                [field]: value
                                            }))}
                                            required
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Col><Label>Password</Label></Col>
                                        <div className="form-input position-relative">
                                            <PasswordInput
                                                name="password"
                                                value={formData.password}
                                                setValue={(field, value) => setFormData(prev => ({
                                                    ...prev,
                                                    [field]: value
                                                }))}
                                                showPassword={isPasswordVisible}
                                                required
                                                maxChars={30}
                                            />
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <Col><Label>Confirm Password</Label></Col>
                                        <div className="form-input position-relative">
                                            <PasswordInput
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                setValue={(field, value) => setFormData(prev => ({
                                                    ...prev,
                                                    [field]: value
                                                }))}
                                                showPassword={isConfirmPasswordVisible}
                                                required
                                                maxChars={30}
                                            />
                                        </div>
                                    </FormGroup>

                                    <div className="d-flex align-items-center gap-4 mb-3">
                                        <FormGroup check className="mb-0">
                                            <Input
                                                type="checkbox"
                                                checked={consent}
                                                onChange={(e) => setConsent(e.target.checked)}
                                                required
                                            />
                                            <Label check style={{ color: '#0d6efd' }}>I consent</Label>
                                        </FormGroup>

                                        {/* <FormGroup check className="mb-0">
                                            <Input
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                            />
                                            <Label check style={{ color: '#0d6efd' }}>Remember me</Label>
                                        </FormGroup> */}

                                        {/* <Link to="/forgotpassword" style={{ color: '#0d6efd', textDecoration: 'none' }}>
                                            Forgot Password
                                        </Link> */}
                                    </div>

                                    <p className="text-center mb-3" style={{ fontSize: '14px', color: '#6c757d' }}>
                                        By signing up or logging into the Mortgage Mastery Program you consent and agree
                                        to the <b><a href="https://www.fintrack.com.au/terms-conditions.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 'bold' }}>terms & conditions</a>, <a href="https://www.fintrack.com.au/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 'bold' }}>privacy</a> and <a href="https://www.fintrack.com.au/disclaimer.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 'bold' }}>disclaimer</a></b> applicable to this program.
                                    </p>

                                    <FormGroup className="mb-0">
                                        <div className="text-end mt-3">
                                            <Btn color='primary' className="w-100" block type="submit" disabled={isPending}>
                                                {isPending ? "Loading . . ." : "Sign Up"}
                                            </Btn>
                                        </div>
                                    </FormGroup>

                                    <div className="text-center mt-3">
                                        <span>Already have an account? </span>
                                        <Link to="/MMPLogin">Login</Link>
                                    </div>

                                    <div style={{ fontSize: '12px', textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
                                        <span>Copyright - {new Date().getFullYear()} - Fintrack Pty Ltd ACN 006 164 709.</span>
                                        <br />
                                        <span>All rights reserved.</span>
                                    </div>

                                    <div className="d-flex justify-content-between" style={{ fontSize: '14px' }}>
                                        <a href="https://www.fintrack.com.au/terms-conditions.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 'bold' }}>
                                            Terms & Conditions
                                        </a>
                                        <a href="https://www.fintrack.com.au/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 'bold' }}>
                                            Privacy
                                        </a>
                                        <a href="https://www.fintrack.com.au/disclaimer.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 'bold' }}>
                                            Disclaimer
                                        </a>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
} 